import axios from "axios";
import router from "./router";
import * as jwtDecode from "jwt-decode";

// Create an Axios instance with a base URL
const instance = axios.create({
  baseURL: process.env.VUE_APP_API,
});

// Interceptor to add Authorization header and guid parameter
instance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    const guid = localStorage.getItem("guid");

    if (token) {
      const decodedToken = jwtDecode.jwtDecode(token); // This should work now
      const currentTime = Date.now() / 1000;

      // Check if the token is expired
      if (decodedToken.exp < currentTime) {
        localStorage.removeItem("token");
        router.push("/");
        return Promise.reject(new Error("Token expired"));
      }

      config.headers.Authorization = `Bearer ${token}`;
    }

    if (guid) {
      const url = new URL(config.url, config.baseURL);
      url.searchParams.append("guid", guid);
      config.url = url.toString();
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Generic request handler
const isFormData = (data) => data instanceof FormData;
function apiRequest(method, path, data = null, s, e, config = {}) {
  // Determine content type based on data type
  const headers = isFormData(data)
    ? { "Content-Type": "multipart/form-data" }
    : { "Content-Type": "application/json" };

  const request = instance({
    method,
    url: path,
    data,
    headers: {
      ...headers,
      ...config.headers, // Allow custom headers to override defaults
    },
    ...config,
  });

  if (s || e) {
    return request
      .then((response) => {
        const resp = response.data;
        if (!resp.isError) {
          s && s(resp);
        } else {
          e && e(resp.message);
        }
      })
      .catch((error) => {
        // Check for API error response
        if (error.response && error.response.data) {
          e && e(error.response.data);
          return;
        }

        // Handle network or other errors
        console.error('Request failed:', error);
        e && e({
          errorCode: "REQUEST_FAILED",
          message: "Failed to send request",
          statusCode: error.response?.status || 500,
          details: error.message || "Network error occurred"
        });
      });
  }

  return request
    .then((response) => {
      const resp = response.data;
      if (!resp.isError) {
        return resp;
      } else {
        throw new Error(resp.message);
      }
    })
    .catch((error) => {
      // Check for API error response
      if (error.response && error.response.data) {
        throw error.response.data;
      }

      // Handle network or other errors
      console.error('Request failed:', error);
      throw {
        errorCode: "REQUEST_FAILED",
        message: "Failed to send request",
        statusCode: error.response?.status || 500,
        details: error.message || "Network error occurred"
      };
    });
}

export default {
  // URL for other references
  Url: process.env.VUE_APP_API,

  post(path, data, s, e) {
    return apiRequest("post", path, data, s, e);
  },

  get(path, params, s, e) {
    return apiRequest("get", path, null, s, e, { params });
  },

  put(path, data, s, e) {
    return apiRequest("put", path, data, s, e);
  },

  delete(path, data, s, e) {
    return apiRequest("delete", path, data, s, e);
  },

  // Specialized methods using the generic ones
  getMicroData(jobID, s, e) {
    return this.get(`/Microcat/GetJobData?jobId=${jobID}`, null, s, e);
  },

  isMicroDone(jobID, s, e) {
    return this.get(`/Microcat/IsOrderDone?jobId=${jobID}`, null, s, e);
  },
  getQuoteData(uploadID, s, e) {
    return this.get(`/AudatexQuote/GetData?id=${uploadID}`, null, s, e);
  },

  isDone(uploadID, s, e) {
    return this.get(`/AudatexQuote/IsDone?id=${uploadID}`, null, s, e);
  },
  startManualQuote(caseType) {
    const formData = new FormData();
    formData.append("NewVehicle", "true");
    formData.append("Manual", "false");
    formData.append("QuoteType", caseType);
 

    var url = `${this.Url}/AudatexQuote/StartManualQuote`;  
    return this.post(`${url}`, formData);
  },
  startQuote(caseType, pdf,url) {
    const formData = new FormData();
    formData.append("NewVehicle", "true");
    formData.append("Manual", "false");
    formData.append("QuoteType", caseType);
    formData.append("file", pdf);

     url = `${this.Url}${url}`;  
    return this.post(`${url}`, formData);
  },
  finishQuote(payload,uploadID, s, e) {
    return this.post(`/AudatexQuote/FinishQuote?id=${uploadID}`, payload, s, e);
  },

  fileUpload(formData, s, e) {
    return this.post("/Notes/FileUpload", formData, s, e);
  },

  deleteQuote(duplicateId, s, e) {
    return this.delete(`/AudatexQuote/Delete?id=${duplicateId}`, null, s, e);
  },

  getEntities(s, e) {
    return this.get("/Entity/GetEntities", null, s, e);
  },

  getEntityPermissions(s, e) {
    return this.get("/Entity/GetPermissions", null, s, e);
  },

  getPSDEmail(psd, s, e) {
    const psdNoSpace = psd.replace(/ /g, "%");
    return this.get(`/Psd/Get?name=${psdNoSpace}`, null, s, e);
  },

  startBusinessUserRegistration(email, s, e) {
    return this.get(
      `/ApprovalRegistration/StartBusinessUser?email=${email}`,
      null,
      s,
      e
    );
  },

  finishPasswordResset(key, password, s, e) {
    return axios.post(
      `${this.Url}/PasswordReset/Finish`,
      {
        key: key,
        password: password
      },
      {
        headers: {
          'Content-Type': 'application/json'
        }
      }
    ).then(response => s(response.data)).catch(error => e(error));
  },
  getCompanies(s, e, page = 1, pageSize = 10, searchTerm = "") {
    return this.get(
      `/Companies?page=${page}&pageSize=${pageSize}&searchTerm=${searchTerm}`,
      null,
      s,
      e
    );
  },

  // Add this method to the export default object

  getCompanyById(companyId,s, e) {
    return this.get(`/Companies/${companyId}`, null, s, e);
  },

  createCompany(companyData,s, e) {
    return this.post("/Companies", companyData, s, e);
  },

  // Update existing company
  updateCompany(companyId, companyData,s, e) {
    return this.put(`/Companies/${companyId}`, companyData, s, e);
  },

  // Delete company
  deleteCompany(s, e, companyId) {
    return this.delete(`/Companies/${companyId}`, null, s, e);
  },
  CaseClearParts(id, s, e) {
    return this.get(`/AudatexQuote/ClearParts/${id}`, null, s, e);
  },
  startPasswordResset(email, s, e) {
    return this.post(
        "/PasswordReset/Start",
        { email: email },
        s,
        e
    );
  },

  getMetaData(type, s, e) {
    return this.get(`/Crud/${type}`, null, s, e);
  },

  getNotes(type, s, e) {
    return this.get(`/Bucket/${type}`, null, s, e);
  },

  crudRead(type, s, e, userId = -1, page = 1, pageSize = 100000000) {
    return this.get(
      `/Crud/${type}?page=${page}&pageSize=${pageSize}&userid=${userId}`,
      null,
      s,
      e
    );
  },

  crudReadPaging(
    type,
    page,
    pageSize,
    search,
    searchFields,
    s,
    e,
    userId = -1
  ) {
    return this.get(
      `/Crud/${type}?userid=${userId}&page=${page}&pageSize=${pageSize}&search=${search}&searchFields=${searchFields}`,
      null,
      s,
      e
    );
  },

  getUsers(s, e, page = 1, pageSize = 10, searchTerm = "") {
    return this.get(
      `/Users?page=${page}&pageSize=${pageSize}&searchTerm=${searchTerm}`,
      null,
      s,
      e
    );
  },
  // Fixed User CRUD methods with consistent parameter ordering

  getUser(s, e, userId) {
    return this.get(`/Users/${userId}`, null, s, e);
  },

  createUser(s, e, userData) {
    return this.post("/Users", userData, s, e);
  },

  updateUser(s, e, userId, userData) {
    return this.put(`/Users/${userId}`, userData, s, e);
  },

  deleteUser(s, e, userId) {
    return this.delete(`/Users/${userId}`, null, s, e);
  },
  getPartsSupplyDealers(s, e, page = 1, pageSize = 10, searchTerm = "") {
    return this.get(
      `/PartsSupplyDealers?page=${page}&pageSize=${pageSize}&searchTerm=${searchTerm}`,
      null,
      s,
      e
    );
  },
  // API method
getPartsSupplyDealer(id, s, e) {
  return this.get(
      `/PartsSupplyDealers/${id}`,
      null,
      s,
      e
  );
},
  createPartsSupplyDealer(psd, s, e) {
    return this.post("/PartsSupplyDealers", psd, s, e);
  },

  updatePartsSupplyDealer(publicId, psd, s, e) {
    return this.put(`/PartsSupplyDealers/${publicId}`, psd, s, e);
  },

  deletePartsSupplyDealer(publicId, s, e) {
    return this.delete(`/PartsSupplyDealers/${publicId}`, null, s, e);
  },

  login(email, password, s, e) {
    const bodyData = { Email: email, Password: password };
    return this.post(
      "/Login",
      bodyData,
      (resp) => {
        localStorage.clear();
        localStorage.setItem("user", JSON.stringify(resp.body));
        localStorage.setItem("guid", resp.body.apiKey);
        localStorage.setItem("token", resp.body.token);
        localStorage.setItem("logedin", true);
        s(resp.message);
      },
      e
    );
  },

  isLogedin() {
    return localStorage.getItem("logedin") === "true";
  },

  logout() {
    localStorage.setItem("user", null);
    localStorage.setItem("logedin", "false");
  },

  sendMail(data, s, e) {
    return this.post("/Notification/SendMail", data, s, e);
  },

  sendPSDEmails(data, s, e) {
    return this.post("/Notification/SendPSDEmail", data, s, e);
  },

  sendHARPMail(data, s, e) {
    return this.post("/Notification/SendMailToAllAdmins", data, s, e);
  },

  saveNewNote(data, s, e) {
    return this.post("/Notes/CreateNote", data, s, e);
  },

  GetQuotes(status, page = 1, pageSize = 10, search = "", s, e) {
    return this.get(
      `/AudatexQuote/read/${status}?page=${page}&pageSize=${pageSize}&search=${search}`,
      null,
      s,
      e
    );
  },
  uploadInvoice(data, s, e) {
    return this.post(`/AudatexQuote/invoices`, data, s, e);
  },
  getInvoices(id, s, e) {
    return this.get(`/AudatexQuote/invoices/${id}`, null, s, e);
  },
  GetQuote(id, s, e) {
    return this.get(`/AudatexQuote/item/${id}`, null, s, e);
  },

  MakeClone(data, s, e) {
    return this.post(`/AudatexQuote/MakeClone/?id=${data.id}`, data, s, e);
  },

  DeleteQuote(id, s, e) {
    return this.delete(`/AudatexQuote/DeleteQuote/${id}`, null, s, e);
  },

  CaseUpdate(data, email, s, e) {
    if (!email || email == "") {
      email = "None";
    }
    return this.post(
      `/AudatexQuote/UpdateQuote/?id=${data.id}&email=${email}`,
      data,
      s,
      e
    );
  },

  updateParts(quoteId, partsData, s, e) {
    return this.post(
      `/AudatexQuote/UpdateParts/${quoteId}`,
      partsData,
      s,
      e
    );
  },

  GetDashboardSummary(s, e) {
    return this.get("/Reporting/DashboardSummary", null, s, e);
  },

  CaseStatusReport(s, e, type) {
    return this.get(`/Report/${type}`, null, s, e);
  },

  // Authenticated file download. Bypasses apiRequest's .data/.isError
  // unwrapping because the response is a binary blob, not JSON.
  // Uses the shared `instance`, so the Auth0 token interceptor and
  // VUE_APP_API baseURL both apply automatically.
  downloadBlob(path, filename) {
      return instance
          .get(path, { responseType: "blob" })
          .then((response) => {
              const url = window.URL.createObjectURL(new Blob([response.data]));
              const link = document.createElement("a");
              link.href = url;
              link.setAttribute("download", filename);
              document.body.appendChild(link);
              link.click();
              link.remove();
              window.URL.revokeObjectURL(url);
          });
  },

  DownloadReport(s, e, type) {
    return this.get(`/Report/${type}?export=true`, null, s, e);
  },

  CaseStatusReportPerCompany(s, e, type, comp) {
    return this.get(`/Report/${type}?q=${comp}`, null, s, e);
  },

  crudReadSingle(type, id, s, e) {
    return this.get(`/Crud/${type}?id=${id}`, null, s, e);
  },

  completeBusinessUserRegistration(
    guid,
    title,
    name,
    surname,
    email,
    password,
    number,
    role,
    company,
    s,
    e
  ) {
    const jsonData = {
      Title: title,
      Name: name,
      Surname: surname,
      Email: email,
      PhoneNumber: number,
      Entity: role,
      Company: company,
      password: password,
      guid: guid
    };

    return this.post("/ApprovalRegistration/Complete", jsonData, s, e);
  },

  CreateEntity(entity, s, e) {
    return this.post("/Entity/Create", entity, s, e);
  },

  CrudNew(type, bodyFormData, s, e) {
    return this.post(`/Crud/${type}`, bodyFormData, s, e);
  },

  CrudUpdate(type, id, data, s, e) {
    return this.put(`/Crud/${type}?id=${id}`, data, s, e);
  },

  GetPsdEmails(psdId, s, e) {
    return this.get(`/PartsSupplyDealers/${psdId}/user-emails`, null, s, e);
  },
};
